import { FC, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import LoadingPanel from "../../../custom-components/CustomLoader";
import { NullableCollection, ResponseMessageObj, defaultResponseMessageObj } from "../../../types/ApiResults";
import { DEFAULT_PAGE } from "../../../domain/constants";
import { PaginationEnum } from "../../../domain/enums/common/PaginationEnum";
import { ResponseMessage } from "../../../domain/enums/common/ResponseMessageEnum";
import Toast from "../../../custom-components/Toast";
import Pagination from "../../../custom-components/CustomPagination";
import UsersTable from "./UsersTable";
import { User, UsersCollection } from "../../../domain/models/User";
import { userServiceFactory } from "../../../services/UserServiceImpl";
import UsersHeader from "./UsersHeader";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { UserDTO } from "../../../domain/models/dto/UserDTO";
import { SelectValue } from "../../../types/SelectProps";
import {
    usersSearchFiltersFields,
    usersSearchFiltersSchemaFactory,
} from "../../../validation-schemas/UsersSearchFiltersFields";
import { useTranslation } from "react-i18next";

const userService = userServiceFactory();

const UsersPage: FC = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState<UsersCollection>(NullableCollection);

    const [selectedUser, setSelectedUser] = useState<User>(null);

    const [reloadPage, setReloadPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [currentLimit, setCurrentLimit] = useState(PaginationEnum.SHOW_20);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(defaultResponseMessageObj);

    const getUsers = async (filterValues?: UserDTO) => {
        setLoading(true);
        const usersResult = await userService.getAllUsers(currentPage, currentLimit, filterValues);
        if (usersResult) {
            setUsers(usersResult);
            resetSelectedUser();
            setReloadPage(false);
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.SUCCESS,
                messageKey: "messages.received",
            });
        } else {
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.DANGER,
                messageKey: "errors.common",
            });
        }
        setLoading(false);
    };

    const [searchFiltersFields, setSearchFiltersFields] = useState<UserDTO>({} as UserDTO);
    const searchFiltersForm = useCustomFormik<UserDTO>({
        initial: null,
        fields: usersSearchFiltersFields,
        schema: usersSearchFiltersSchemaFactory(t),
        onSubmit: async (values) => {
            setSearchFiltersFields(values);
            setCurrentPage(DEFAULT_PAGE);
            await getUsers(values);
        },
    });

    useEffect(() => {
        getUsers(searchFiltersFields);
    }, [currentLimit, currentPage, reloadPage]);

    const limitHandler = (value: SelectValue) => {
        setCurrentLimit(value as number);
    };

    const pageHandler = (value: number) => {
        setCurrentPage(value);
    };

    const resetSelectedUser = () => {
        setSelectedUser(null);
    };

    const resetMessageObj = () => {
        setMessageObj(defaultResponseMessageObj);
    };

    const resetFiltersHandler = () => {
        searchFiltersForm.form.resetForm();
        setSearchFiltersFields({} as UserDTO);
        setReloadPage(true);
    };

    return (
        <div className="relative z-0 bg-light-bg-700 dark:bg-dark-bg-800">
            <LoadingPanel isVisible={loading} />
            <UsersHeader
                selectedUser={selectedUser}
                searchFiltersForm={searchFiltersForm.form}
                filtersFields={searchFiltersFields}
                resetSelectedUser={resetSelectedUser}
                limitHandler={limitHandler}
                resetFiltersHandler={resetFiltersHandler}
            />
            <UsersTable users={users.rows} updateHandler={setSelectedUser} />
            <Pagination currentPage={currentPage} totalPages={users.totalPages} pageHandler={pageHandler} />
            <Toast messageObj={messageObj} resetMessageObj={resetMessageObj} />
        </div>
    );
};

export default UsersPage;
